import { Back, Elastic, Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GameType } from "../Data/GameData";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import { Andy } from "../GameObjects/Andy";
import { Button } from "../GameObjects/Button";
import { GalleryDoll } from "../GameObjects/GalleryDoll";
import { GalleryDress } from "../GameObjects/GalleryDress";
import { GalleryHieroglyph } from "../GameObjects/GalleryHieroglyph";
import { GalleryItem } from "../GameObjects/GalleryItem";
import { GalleryMask } from "../GameObjects/GalleryMask";
import { GalleryQuilt } from "../GameObjects/GalleryQuilt";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { GameHieroglyph } from "./GameHieroglyph";
import { GameQuilt } from "./GameQuilt";

export class Menu extends HLF.State {
    private _andy!: Andy;
    private _galleryItems!: GalleryItem[];
    private _bg!: PIXI.Sprite;

    private _overlay!: PIXI.Sprite;
    private _blackout!: PIXI.Sprite;
    private _buttonContinue!: Button;
    private _keyboardFocusManager!: KeyboardFocusManager;

    constructor() {
        super();

        this._bg = HLF.PixiUtils.makeSprite("menu-bg");
        this.addChild(this._bg);

        this._galleryItems = new Array<GalleryItem>(GameType.NUM_ELEMENTS);
        this._galleryItems[GameType.DOLL] = new GalleryDoll(this.onGalleryItemPressed.bind(this));
        this._galleryItems[GameType.DRESS] = new GalleryDress(this.onGalleryItemPressed.bind(this));
        this._galleryItems[GameType.HIEROGLYPH] = new GalleryHieroglyph(this.onGalleryItemPressed.bind(this));
        this._galleryItems[GameType.MASK] = new GalleryMask(this.onGalleryItemPressed.bind(this));
        this._galleryItems[GameType.QUILT] = new GalleryQuilt(this.onGalleryItemPressed.bind(this));
        for (let i = 0; i < this._galleryItems.length; ++i) {
            this.addChild(this._galleryItems[i]);
        }
        HLF.PixiUtils.sendToFront(this._galleryItems[GameType.MASK]);

        this._blackout = HLF.PixiUtils.makeSprite("black2x2", Constants.SPRITESHEET_UI);
        this._blackout.interactive = true;
        this._blackout.visible = false;
        this.addChild(this._blackout);

        this._andy = new Andy();
        this.addChild(this._andy);

        if (AppProgress.recentGame !== undefined) {
            HLF.PixiUtils.sendToFront(this._galleryItems[AppProgress.recentGame]);
        }

        this._buttonContinue = new Button(
            this.onContinuePressed.bind(this),
            "button-continue", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this._buttonContinue.visible = false;
        this.addChild(this._buttonContinue);

        this._keyboardFocusManager = new KeyboardFocusManager();
        this.addChild(this._keyboardFocusManager);

        this._overlay = HLF.PixiUtils.makeSprite("black2x2", Constants.SPRITESHEET_UI);
        this._overlay.interactive = true;
        this._overlay.visible = false;
        this.addChild(this._overlay);

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        PubSub.publishSync(Topics.GALLERY_ENTERED, undefined);
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "menu-bg.jpg",
            "menu.json",
        ];
        switch (AppProgress.recentGame) {
            case GameType.DOLL:
                requiredAssets.push("doll-0" + AppProgress.games[AppProgress.recentGame].recentItemIndex + ".json");
                break;
            case GameType.DRESS:
                requiredAssets.push("dress-0" + AppProgress.games[AppProgress.recentGame].recentItemIndex + ".json");
                break;
            case GameType.HIEROGLYPH:
                requiredAssets.push(...GameHieroglyph.getRequiredAssets());
                break;
            case GameType.MASK:
                requiredAssets.push("mask-0" + AppProgress.games[AppProgress.recentGame].recentItemIndex + ".json");
                break;
            case GameType.QUILT:
                requiredAssets.push(...GameQuilt.getRequiredAssets());
                break;
        }
        return requiredAssets;
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);
        this._buttonContinue.position.set(0, this._size.y * 0.5 - this._buttonContinue.height * 0.5 - 25);
    }

    private get completedItem(): GalleryItem | undefined {
        const completedGameType: GameType | undefined = AppProgress.recentGame;
        if (completedGameType !== undefined) {
            return this._galleryItems[completedGameType];
        }
        return undefined;
    }

    public update(pDeltaTime: number) {
        if (this._andy !== undefined) {
            this._andy.update(pDeltaTime);
        }
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this._blackout.scale.set(this._size.x, this._size.y);
        this._overlay.scale.set(this._size.x, this._size.y);
        this._buttonContinue.position.set(0, this._size.y * 0.5 - this._buttonContinue.height * 0.5 - 25);

        this._andy.position.set(
            this._size.x * 0.5 - this._andy.width * 0.9,
            this._size.y * 0.5 + this._andy.height * 0.3,
        );
    }

    public animateIn(pOnComplete: () => void): void {
        super.animateIn(pOnComplete);
        this.scale.set(1.05);
        this._overlay.alpha = 1;
        this._overlay.visible = true;

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._overlay, 1.0, {
            alpha: 0,
            ease: Sine.easeInOut,
            onComplete: () => { this._overlay.visible = false; },
        }, 0);
        timeline.to(this.scale, 1.0, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        }, 0);

        const item: GalleryItem | undefined = this.completedItem;
        if (item !== undefined) {
            this._blackout.visible = true;
            this._blackout.alpha = 0.75;

            timeline.add(item.presentItem(), 0);
            timeline.delay(0.25);
            timeline.add(item.animateItem());
            timeline.delay(0.5);
            timeline.add(this.showContinueButton());
            timeline.call(() => {
                PubSub.publishSync(Topics.GALLERY_ITEM_FINISHED, undefined);
            });
        }
        else {
            timeline.call(() => {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._galleryItems);
            });
        }
        timeline.call(pOnComplete);
    }

    public animateOut(pOnComplete: () => void): void {
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        this._overlay.visible = true;
        HLF.PixiUtils.sendToFront(this._overlay);

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._overlay, 1.0, {
            alpha: 1,
            ease: Sine.easeInOut,
            onComplete: () => { this._overlay.visible = false; },
        }, 0);
        timeline.to(this.scale, 1.0, {
            x: 1.05,
            y: 1.05,
            ease: Sine.easeInOut,
        }, 0);
        timeline.call(pOnComplete);
    }

    private hideBlackout(pDur: number): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._blackout, pDur, {
            alpha: 0,
            ease: Sine.easeInOut,
        });
        timeline.call(() => {
            this._blackout.visible = false;
        });
        return timeline;
    }

    private showContinueButton(): TimelineLite {
        this._buttonContinue.visible = true;
        this._buttonContinue.alpha = 0;
        this._buttonContinue.scale.set(0);
        this._buttonContinue.interactive = false;

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._buttonContinue, 0.5, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, 0);
        timeline.to(this._buttonContinue.scale, 0.5, {
            x: 1,
            y: 1,
            ease: Elastic.easeOut,
        }, 0);
        timeline.call(() => {
            this._buttonContinue.interactive = true;
            PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._buttonContinue);
        });
        return timeline;
    }

    private hideContinueButton(): TimelineLite {
        this._buttonContinue.interactive = false;
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._buttonContinue, 0.4, {
            y: this._size.y * 0.5 + this._buttonContinue.height,
            ease: Back.easeIn,
        });
        timeline.call(() => {
            this._buttonContinue.visible = false;
        });
        return timeline;
    }

    private cacheCompletedItemVisuals(): void {
        const item: GalleryItem = this.completedItem!;
        AppProgress.games[AppProgress.recentGame!].cachedGalleryVisuals = item.cacheVisuals();
    }

    private onGalleryItemPressed(pItem: GalleryItem): void {
        PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(pItem.targetState, Constants.LOAD_INTERSTITIAL));
    }

    private onContinuePressed(): void {
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLE, this._buttonContinue);

        const item: GalleryItem = this.completedItem!;
        const timeline: TimelineLite = new TimelineLite();
        timeline.add(item.moveItemToWall(), 0);
        // Pass in the duration of the moveItemToWall animation so the blackout is synced
        const moveDur: number = timeline.duration();
        timeline.add(this.hideBlackout(moveDur), 0);
        timeline.add(item.performHangUp());
        // Fix for mask/quilt layering conflict
        timeline.call(() => {
            HLF.PixiUtils.sendToFront(this._galleryItems[GameType.MASK]);
        });
        timeline.add(this.hideContinueButton(), 0);
        timeline.call(() => {
            this.cacheCompletedItemVisuals();
            PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._galleryItems);
        });
    }
}
